import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/pro-solid-svg-icons';

import {ErrorBanner} from '../../../shared/components';
import {SupplierChipsSelector} from '../components';

interface IPrimarySupplierProduct {
  id: number | string;
  name: string;
}

interface IPrimarySupplierOption {
  id: number | string;
  name: string;
  products: IPrimarySupplierProduct[];
}

export interface IPrimarySuppliersSubStepProps {
  isLoadingPrimarySuppliers: boolean;
  totalPrimarySupplierSelections: number;
  primarySupplierDeleteQueueLength: number;
  sortedPrimarySuppliers: IPrimarySupplierOption[];
  expandedPrimarySupplierId: number | null;
  primarySupplierSelectionMap: Map<number, Set<number>>;
  isSavingPrimarySuppliers: boolean;
  primarySuppliersError: string | null;
  hasApplicationId: boolean;
  onTogglePrimarySupplier: (supplierId: number) => void;
  onSelectPrimarySupplierProduct: (
    supplierId: number,
    supplierProductId: number,
  ) => void;
  onDeselectPrimarySupplierProduct: (
    supplierId: number,
    supplierProductId: number,
  ) => void;
  isPrimarySupplierItemQueuedForDelete: (
    supplierId: number,
    supplierProductId: number,
  ) => boolean;
}

export const PrimarySuppliersSubStep: React.FC<
  IPrimarySuppliersSubStepProps
> = ({
  isLoadingPrimarySuppliers,
  totalPrimarySupplierSelections,
  primarySupplierDeleteQueueLength,
  sortedPrimarySuppliers,
  expandedPrimarySupplierId,
  primarySupplierSelectionMap,
  isSavingPrimarySuppliers,
  primarySuppliersError,
  hasApplicationId,
  onTogglePrimarySupplier,
  onSelectPrimarySupplierProduct,
  onDeselectPrimarySupplierProduct,
  isPrimarySupplierItemQueuedForDelete,
}) => (
  <div className="primary-suppliers-step">
    <header className="primary-suppliers-step__intro">
      <h3>Who are your primary suppliers?</h3>
      <p>
        Select your suppliers and choose the products you typically buy from
        them.
      </p>
    </header>

    <div className="primary-suppliers-step__status" aria-live="polite">
      {isLoadingPrimarySuppliers ? (
        <span className="primary-suppliers-step__status-message">
          <FontAwesomeIcon icon={faSpinner} spin role="status" />
          Loading suppliers...
        </span>
      ) : (
        <span>
          {totalPrimarySupplierSelections > 0
            ? `${totalPrimarySupplierSelections} ${
                totalPrimarySupplierSelections === 1
                  ? 'selection'
                  : 'selections'
              } captured`
            : 'No supplier selections captured yet'}
        </span>
      )}

      {primarySupplierDeleteQueueLength > 0 ? (
        <span className="primary-suppliers-step__status-queued">
          {primarySupplierDeleteQueueLength}{' '}
          {primarySupplierDeleteQueueLength === 1 ? 'item' : 'items'} queued for
          deletion
        </span>
      ) : null}
    </div>

    <section
      className="primary-suppliers-step__list"
      aria-live="polite"
      aria-busy={isLoadingPrimarySuppliers}
    >
      {sortedPrimarySuppliers.filter((supplier) => supplier.products.length > 0)
        .length === 0 && !isLoadingPrimarySuppliers ? (
        <p className="primary-suppliers-step__empty">
          We could not find any suppliers linked to your profile.
        </p>
      ) : null}

      {sortedPrimarySuppliers
        .filter((supplier) => supplier.products.length > 0)
        .map((supplier) => {
          const supplierId = Number(supplier.id);
          const selectedSet = primarySupplierSelectionMap.get(supplierId);
          const selectedCount = selectedSet ? selectedSet.size : 0;
          const expanded = expandedPrimarySupplierId === supplierId;

          return (
            <SupplierChipsSelector
              key={supplier.id}
              supplier={supplier}
              expanded={expanded}
              selectedCount={selectedCount}
              selectedSet={selectedSet}
              products={supplier.products}
              isSaving={isSavingPrimarySuppliers}
              onToggle={onTogglePrimarySupplier}
              onSelect={onSelectPrimarySupplierProduct}
              onDeselect={onDeselectPrimarySupplierProduct}
              isQueuedForDelete={isPrimarySupplierItemQueuedForDelete}
            />
          );
        })}
    </section>

    <ErrorBanner message={primarySuppliersError} />

    {!hasApplicationId ? (
      <ErrorBanner
        message={
          'We could not determine your application. Please return to the previous step and try again.'
        }
      />
    ) : null}
  </div>
);
